import {
	Flex,
	Text,
	READABLE_LINE_MAX_WIDTH,
	Button,
} from "@capioweb/ui-components/src"
import { BlockCommonLayout } from "../../utils/layout"
import { BreadcrumbsBlock } from "../../components"
import { BreadcrumbItemProps } from "../../components/Core"
import { NewsFeedItemBlock } from "./NewsFeedItemBlock"
import { LocalesContext } from "../../context"
import React from "react"
import { QBankImage } from "../../@types/CapioWeb/core"
import { useLocation } from "react-router-dom"
import { scrollToAnchor } from "../../utils/scrollToAnchor"

interface NewsFeedPageProps {
	breadcrumb: Array<BreadcrumbItemProps>
	items: NewsFeedItemProps[]
}
export interface NewsFeedItemProps {
	heading: string
	image: QBankImage
	preamble: string
	body: string
	editor: string
	date: string
	anchorId: string
	pathname: string
}

interface LatestNewsItemProps {
	heading: string
	date: string
	anchorId: string
	lastChild?: boolean
}

const LatestNewsItem: React.FC<LatestNewsItemProps> = ({
	heading,
	date,
	anchorId,
	lastChild,
}) => {
	return (
		<Flex
			mt={2}
			pb={!lastChild && 2}
			borderBottom={!lastChild}
			borderColor="blue66"
			flexDirection="column"
		>
			<Text mb={0}>{date}</Text>
			<Text.Anchor href={`#${anchorId}`} fontWeight="bold" mb={lastChild && 0}>
				{heading}
			</Text.Anchor>
		</Flex>
	)
}

const NewsFeedPage: React.FC<NewsFeedPageProps> = ({ breadcrumb, items }) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const [showAllLatestNews, setShowAllLatestNews] =
		React.useState<boolean>(false)

	const { hash, pathname } = useLocation()

	const id = decodeURI(hash.substring(1))

	const el = document?.getElementById(id)

	React.useEffect(() => {
		if (el) {
			//Scroll to anchorID position if it exists
			scrollToAnchor(el)
		}
	}, [el]) //UseEffect will run again when 'el' is ready

	if (items.length <= 0) return null
	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={breadcrumb} />
			<BlockCommonLayout>
				<Flex columnGap={8} w={1}>
					<Flex flexDirection="column" w={1} maxw={READABLE_LINE_MAX_WIDTH}>
						{items?.map((item, index, array) => (
							<NewsFeedItemBlock
								key={`news-feed-item-${index}`}
								{...item}
								pathname={pathname}
								lastChild={index >= array.length - 1}
							/>
						))}
					</Flex>
					<Flex
						as="aside"
						display={{ _: "none", m: "flex" }}
						backgroundColor="blue33"
						flex={1}
						maxw="296px"
						flexDirection="column"
						borderRadius={24}
						py={6.5}
						px={3}
						h="fit-content"
					>
						<Text as="h3" variant="headline-2" mb={0}>
							{translate(translationKeys.news)}
						</Text>

						{items
							?.slice(0, !showAllLatestNews ? 5 : 15)
							.map((item, index, array) => (
								<LatestNewsItem
									key={`latest-news-item-${index}`}
									lastChild={index >= array.length - 1}
									{...item}
								/>
							))}
						{items.length > 5 && (
							<Button
								icon={showAllLatestNews ? "minus" : "plus"}
								iconPosition="left"
								onClick={() => setShowAllLatestNews(!showAllLatestNews)}
								mt={4}
							>
								{showAllLatestNews
									? translate(translationKeys.showLess)
									: translate(translationKeys.showMore)}
							</Button>
						)}
					</Flex>
				</Flex>
			</BlockCommonLayout>
		</Flex>
	)
}

export { NewsFeedPage }
